// Ordnung der Tab-Leiste (docs/shell.md, „Tabs nach Bereich").
//
// Tim 03.10.2026: „Wir brauchen die Möglichkeit, diese nach Kategorie zu
// ordnen. Ist man auf einem Tab und öffnet dort einen weiteren, dann soll
// der direkt daneben auftauchen. Schließt man den neu geöffneten Tab, dann
// kehrt man auf den ersten Tab zurück." Und: „Wir könnten default die
// Gruppierung annehmen. Wird einer manuell woanders hingeschoben, dann
// bleibt der dort."
//
// Daraus drei Regeln:
//   1. Die Leiste ist nach Bereich geordnet: Startseite, dann die
//      Bereiche in der Reihenfolge der Aktivitätsleiste, dann der Rest.
//   2. Ein neuer Tab landet im Block seines Bereichs -- und zwar direkt
//      rechts neben dem Tab, aus dem er geöffnet wurde, wenn der zum
//      selben Bereich gehört.
//   3. Ein von Hand verschobener Tab (`platziert`) bleibt liegen und zählt
//      nicht mehr zu seinem Block; „Nach Bereich ordnen" holt ihn zurück.
// Dazu: wer einen Tab schließt, kehrt zu dem zurück, aus dem er ihn
// geöffnet hat (`von`), sonst übernimmt wie bisher der linke Nachbar.
//
// Reine Logik ohne Laufzeit-Importe (tab-ordnung.test.mjs).

/** Reihenfolge der Bereiche = SectionId in nav-sections.ts (Werkstatt seit
 *  18.10.2026 gleich hinter Zuhause, Argus seit 28.10.2026 hinter
 *  Sicherheit, wie in der Leiste). */
export const BEREICHS_FOLGE: readonly string[] = ["home", "werkstatt", "infra", "sec", "argus", "system", "daten", "claude"];

/** 0 = Startseite, 1-8 = Bereiche, 9 = alles ohne Bereich (Einstellungen,
 *  Konto). */
export function rangVon(bereich: string | undefined, istStart: boolean): number {
  if (istStart) return 0;
  const i = bereich ? BEREICHS_FOLGE.indexOf(bereich) : -1;
  return i >= 0 ? i + 1 : BEREICHS_FOLGE.length + 1;
}

export interface OrdTab {
  id: string;
  rang: number;
  /** Von Hand hingelegt: bleibt, wo er liegt, und zählt nicht zum Block. */
  platziert?: boolean;
}

/** Wohin kommt ein neuer Tab? `platziert` wird geerbt, wenn er neben
 *  einem von Hand verschobenen Tab desselben Bereichs landet -- er gehört
 *  dann zu dem, nicht zum Block. */
export function einfuegeStelle(
  tabs: readonly OrdTab[],
  rang: number,
  oeffnerId: string | null,
): { stelle: number; platziert: boolean } {
  // Gleicher Bereich wie der Öffner: direkt rechts daneben.
  const o = oeffnerId ? tabs.findIndex((t) => t.id === oeffnerId) : -1;
  if (o >= 0 && tabs[o].rang === rang) return { stelle: o + 1, platziert: !!tabs[o].platziert };
  // Sonst ans Ende des Blocks seines Bereichs.
  let letzter = -1;
  tabs.forEach((t, i) => {
    if (!t.platziert && t.rang === rang) letzter = i;
  });
  if (letzter >= 0) return { stelle: letzter + 1, platziert: false };
  // Noch kein Block: vor den ersten Block mit höherem Rang, sonst ans Ende.
  const naechster = tabs.findIndex((t) => !t.platziert && t.rang > rang);
  return { stelle: naechster >= 0 ? naechster : tabs.length, platziert: false };
}

/** Alle Tabs nach Bereich, innerhalb eines Bereichs in bisheriger Folge. */
export function nachBereich<T extends { rang: number }>(tabs: readonly T[]): T[] {
  return tabs
    .map((t, i) => ({ t, i }))
    .sort((a, b) => a.t.rang - b.t.rang || a.i - b.i)
    .map((x) => x.t);
}

/** Ist die Leiste schon geordnet (und liegt nichts von Hand woanders)? */
export function istGeordnet(tabs: readonly OrdTab[]): boolean {
  return tabs.every((t, i) => !t.platziert && (i === 0 || tabs[i - 1].rang <= t.rang));
}

/** Wer übernimmt, wenn der aktive Tab geschlossen wurde? `rest` ist die
 *  Liste OHNE ihn, `idx` seine alte Stelle. Zuerst der Tab, aus dem er
 *  geöffnet wurde; gibt es den nicht mehr, der linke Nachbar. */
export function nachfolger(rest: readonly { id: string }[], idx: number, von: string | undefined): string | null {
  if (von && rest.some((t) => t.id === von)) return von;
  return (rest[idx - 1] ?? rest[idx] ?? null)?.id ?? null;
}
